/**
 * ResetPasswordPage
 * CCH Care — Feature #11
 * Created: 2026-04-21
 *
 * Public page. Reached via email magic link: /reset-password?token=xxx
 *
 * Flow:
 * 1. On mount, GET /auth/reset-password.php?token=xxx to validate.
 *    Backend returns { valid, email (masked), expires_in_minutes }.
 * 2. If invalid/expired, show error + link back to forgot-password.
 * 3. If valid, show masked email + password form + live countdown.
 * 4. On submit, POST /auth/reset-password.php. On success, redirect to
 *    /login?reset=success — backend does NOT mint a session.
 *
 * Client-side password strength hints are advisory; authoritative
 * validation happens server-side in validatePassword().
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { validateResetToken, resetPassword } from '../../api/auth';
import { COLORS } from '../../config/constants';

// Mirror the backend's basic password requirements for early feedback.
// Server is the source of truth; this is UX polish.
const PW_MIN_LENGTH = 8;

function evaluatePassword(pw) {
  const checks = {
    length:    pw.length >= PW_MIN_LENGTH,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    digit:     /\d/.test(pw),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  return { checks, passed };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [validating, setValidating]         = useState(true);
  const [tokenValid, setTokenValid]         = useState(false);
  const [tokenError, setTokenError]         = useState('');
  const [maskedEmail, setMaskedEmail]       = useState('');
  const [minutesLeft, setMinutesLeft]       = useState(null);

  const [password, setPassword]             = useState('');
  const [confirm, setConfirm]               = useState('');
  const [showPassword, setShowPassword]     = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [submitError, setSubmitError]       = useState('');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenError('No reset token was provided. Please request a new password reset link.');
      setValidating(false);
      return;
    }

    let cancelled = false;
    validateResetToken(token)
      .then(data => {
        if (cancelled) return;
        if (data.success && data.valid) {
          setTokenValid(true);
          setMaskedEmail(data.email || '');
          setMinutesLeft(data.expires_in_minutes ?? null);
        } else {
          setTokenValid(false);
          setTokenError(data.error || 'This reset link is invalid or has expired.');
        }
      })
      .catch(err => {
        if (cancelled) return;
        setTokenValid(false);
        setTokenError(
          err?.response?.data?.error
          || 'This reset link is invalid or has expired. Please request a new one.'
        );
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });

    return () => { cancelled = true; };
  }, [token]);

  // Local countdown — ticks minutesLeft down once per minute so the user
  // sees time shrinking while they're on the page. Best-effort UX only;
  // server enforces actual expiry.
  const minutesLeftRef = useRef(minutesLeft);
  minutesLeftRef.current = minutesLeft;
  useEffect(() => {
    if (!tokenValid || minutesLeft === null) return;
    const interval = setInterval(() => {
      setMinutesLeft(m => (m !== null && m > 0 ? m - 1 : 0));
    }, 60_000);
    return () => clearInterval(interval);
  }, [tokenValid, minutesLeft]);

  const { checks, passed } = evaluatePassword(password);
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = tokenValid && passed === 4 && passwordsMatch && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    if (!passwordsMatch) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (passed < 4) {
      setSubmitError(`Password must be at least ${PW_MIN_LENGTH} characters and include uppercase, lowercase, and a number.`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await resetPassword(token, password);
      if (result.success !== false) {
        navigate('/login?reset=success', { replace: true });
      } else {
        setSubmitError(result.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.error || err.message || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={brandRowStyle}>
          <div style={brandSquareStyle}>🏥</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.authText, letterSpacing: '-0.02em' }}>
            CCH Care
          </div>
        </div>

        {validating && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <div style={{ color: COLORS.authTextMuted, fontSize: 14 }}>Validating reset link…</div>
          </div>
        )}

        {!validating && !tokenValid && (
          <>
            <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
            <h1 style={{ ...headingStyle, textAlign: 'center' }}>Link invalid or expired</h1>
            <p style={{ ...subtitleStyle, textAlign: 'center' }}>{tokenError}</p>
            <button onClick={() => navigate('/forgot-password')} style={primaryButtonStyle}>
              Request a new link
            </button>
            <div style={footerStyle}>
              <Link to="/login" style={footerLinkStyle}>Return to sign in</Link>
            </div>
          </>
        )}

        {!validating && tokenValid && (
          <>
            <h1 style={headingStyle}>Create a new password</h1>
            <p style={subtitleStyle}>
              Resetting password for{' '}
              <strong style={{ color: COLORS.authText, fontFamily: 'monospace' }}>{maskedEmail}</strong>
            </p>

            {minutesLeft !== null && minutesLeft > 0 && (
              <div style={timerBannerStyle}>
                ⏱ This link expires in {minutesLeft} minute{minutesLeft === 1 ? '' : 's'}.
              </div>
            )}
            {minutesLeft === 0 && (
              <div style={errorBannerStyle}>
                ⚠ This link has expired. Please request a new one.
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label htmlFor="reset-password" style={labelStyle}>New password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    style={{ ...inputStyle, paddingRight: 64 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    style={togglePwStyle}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>

                {/* Requirement checklist */}
                {password.length > 0 && (
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <PwCheck ok={checks.length}    label={`${PW_MIN_LENGTH}+ characters`} />
                    <PwCheck ok={checks.uppercase} label="Uppercase letter" />
                    <PwCheck ok={checks.lowercase} label="Lowercase letter" />
                    <PwCheck ok={checks.digit}     label="Number" />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="reset-confirm" style={labelStyle}>Confirm password</label>
                <input
                  id="reset-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  style={inputStyle}
                />
                {confirm.length > 0 && !passwordsMatch && (
                  <div style={{ fontSize: 12, color: COLORS.authErrorText, marginTop: 4 }}>
                    Passwords do not match.
                  </div>
                )}
              </div>

              {submitError && (
                <div style={errorBannerStyle}>{submitError}</div>
              )}

              <button type="submit" disabled={!canSubmit} style={{
                ...primaryButtonStyle,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: canSubmit ? 1 : 0.5,
              }}>
                {submitting ? 'Resetting…' : 'Reset password'}
              </button>
            </form>

            <div style={footerStyle}>
              <Link to="/login" style={footerLinkStyle}>Cancel and return to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PwCheck({ ok, label }) {
  return (
    <div style={{
      fontSize: 11,
      color: ok ? COLORS.authAccent : COLORS.authTextHelper,
      display: 'flex',
      alignItems: 'center',
      gap: 5,
    }}>
      <span>{ok ? '✓' : '○'}</span>
      {label}
    </div>
  );
}

// ── Styles (shared pattern with ForgotPasswordPage) ──────────
const pageStyle = {
  minHeight: '100vh',
  background: COLORS.authBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const cardStyle = {
  width: '100%',
  maxWidth: 420,
  background: COLORS.authSurface,
  border: `1px solid ${COLORS.authSurfaceBorder}`,
  borderRadius: 14,
  padding: '32px 28px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};

const brandRowStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 };
const brandSquareStyle = {
  width: 36, height: 36, borderRadius: 8, background: COLORS.authBrandBg,
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
};

const headingStyle = {
  fontSize: 22, fontWeight: 700, color: COLORS.authText,
  margin: '0 0 8px', letterSpacing: '-0.01em',
};

const subtitleStyle = {
  fontSize: 14, color: COLORS.authTextMuted, lineHeight: 1.5, margin: '0 0 20px',
};

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: COLORS.authTextLabel,
  display: 'block', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: 8,
  border: `1px solid ${COLORS.authInputBorder}`,
  background: COLORS.authInputBg, color: COLORS.authText,
  fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
};

const togglePwStyle = {
  position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)',
  background: 'none', border: 'none', color: COLORS.authLink,
  fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 6px',
};

const primaryButtonStyle = {
  width: '100%', padding: '12px 20px', borderRadius: 8, border: 'none',
  background: COLORS.authPrimary, color: '#fff',
  fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
};

const timerBannerStyle = {
  padding: '10px 14px', borderRadius: 8,
  background: COLORS.authWarningBg,
  border: `1px solid ${COLORS.authWarningBorder}`,
  color: COLORS.authWarningText,
  fontSize: 13, marginBottom: 16,
};

const errorBannerStyle = {
  padding: '10px 14px', borderRadius: 8,
  background: COLORS.authErrorBg,
  border: `1px solid ${COLORS.authErrorBorder}`,
  color: COLORS.authErrorText,
  fontSize: 13,
};

const footerStyle = {
  marginTop: 20, textAlign: 'center',
  fontSize: 13, color: COLORS.authTextHelper,
};

const footerLinkStyle = {
  color: COLORS.authLink, textDecoration: 'none', fontWeight: 600,
};